import i18n from './../../../shared/i18n';
import cloneDeep from "lodash/cloneDeep";

import BaseModel, {defineTranslatableProperties} from "../../../core/components/input-widget/BaseModel";
import Premium from "../../../core/components/premium-tiers/Premium.model";
import Commission from "@/core/components/commission-tiers/Commission.model";
import Increment from "@/core/components/increment-tiers/Increment.model";
import helperService from "@/core/services/helper.service";
import {EMAIL_REGEX} from "@/plugins/vee-validate";
import RecipientModel from "@/modules/contracts/Recipient.model";

export default class BranchModel extends BaseModel {
  uuid = null;
  name = '';
  vat_number = null;
  address = null;
  address_2 = null;
  address_3 = null;
  bank_address = null;
  bank_address_2 = null;
  bank_address_3 = null;
  phone = null;
  phone_2 = null;
  mobile = null;
  zip_code = null;
  currency_uuid = null;
  city = null;
  state = null;
  description = null;
  contact_info = null;
  decimal_separator = null;
  thousand_separator = null;
  date_format = null;
  time_format = null;
  number_padding_prefixes = null;
  remove_decimals_on_zero = null;
  country_code = null;
  minimum_commission = null;
  minimum_premium = null;
  tax_status_uuid = null;
  tax_rate = null;
  payment_terms = null;
  settlement_days = null;
  unsold_fee = null;
  withdrawal_fee = null;
  insurance_rate = null;
  lot_handling_charge = null;
  increment_calculation_source = 'bid';
  enable_estimate_request = null;
  premiums = [];
  commissions = [];
  vendor_settlement_footnote = '';
  buyer_invoice_footnote = '';
  logo_uuid = null;
  default_auction_increments = [];
  automatic_deposit_allocation = null;
  auto_approve_invoices = true;
  auto_approve_settlements = true;
  enable_trust_levels = false;
  credit_limit = 0;
  credit_limit_type = 'auction';
  trust_levels = [];
  transfer_note_prefix = null;
  batch_payment_prefix = null;
  email = null;
  telephone_bid_request_cut_off = 0;
  commission_bid_request_cut_off = 0;
  bidder_registration_cut_off = 0;
  contract_default_recipients = []

  constructor(data = {}) {
    super();
    data = helperService.reverseCalculateTiers(data, data.tax_rate)
    Object.assign(this, {...data});

    if (this.premiums.length === 0) {
      this.premiums.push(new Premium({amount_over: null, percent: ''}));
    }

    if (this.commissions.length === 0) {
      this.commissions.push(new Commission({amount_over: null, percent: ''}));
    }

    if (this.default_auction_increments.length === 0) {
      this.default_auction_increments.push(new Increment({max: null, increment: null}));
    }

    if (this.contract_default_recipients) {
      this.contract_default_recipients = this.contract_default_recipients.map(item => {
        return new RecipientModel(item)
      })
    } else {
      this.contract_default_recipients = []
    }

    this.initTranslatableFields(this)
  }

  rules = {
    name: 'required',
    country_code: 'required',
    minimum_commission: 'required',
    credit_limit_type: 'required',
    minimum_premium: 'required',
    settlement_days: 'required',
    payment_terms: 'required',
    currency_uuid: 'required',
    lot_handling_charge: 'required',
    invoice_prefix: 'required',
    settlement_prefix: '',
    receipt_prefix: '',
    insurance_rate: 'required|max_value:100|min_value:0',
    unsold_fee: 'required',
    withdrawal_fee: 'required',
    tax_rate: 'required|max_value:100|min_value:0',
    bank_address: 'max:255',
    bank_address_2: 'max:255',
    bank_address_3: 'max:255',
    increment_calculation_source: 'required',
    email: {
      regex: EMAIL_REGEX,
      max: 73
    },
    telephone_bid_request_cut_off: 'min_value:0',
    commission_bid_request_cut_off: 'min_value:0',
    bidder_registration_cut_off: 'min_value:0'
  };

  static TRANSLATABLE_FIELDS = ['important_information', 'payment_information',
    'viewing_information', 'collection_information', 'terms_and_conditions', 'auction_location', 'auction_contact_information'];

  attributeLabels = {
    uuid: i18n.t('UUID'),
    name: i18n.t('Name'),
    vat_number: i18n.t('VAT Number'),
    address: i18n.t('Address 1'),
    address_2: i18n.t('Address 2'),
    address_3: i18n.t('Address 3'),
    bank_address: i18n.t('Bank Info 1'),
    bank_address_2: i18n.t('Bank Info 2'),
    bank_address_3: i18n.t('Bank Info 3'),
    phone: i18n.t('Phone 1'),
    phone_2: i18n.t('Phone 2'),
    mobile: i18n.t('Mobile'),
    zip_code: i18n.t('Zip/Postcode'),
    currency_uuid: i18n.t('Currency'),
    city: i18n.t('City'),
    state: i18n.t('State/County'),
    description: i18n.t('Description'),
    contact_info: i18n.t('Contact Info'),
    decimal_separator: i18n.t('Decimal Separator'),
    thousand_separator: i18n.t('Thousand Separator'),
    date_format: i18n.t('Date Format'),
    time_format: i18n.t('Time Format'),
    number_padding_prefixes: i18n.t('Number Padding Prefixes'),
    remove_decimals_on_zero: i18n.t('Remove Decimals on Zero'),
    country_code: i18n.t('Country'),
    minimum_commission: i18n.t('Minimum Commission'),
    minimum_premium: i18n.t('Minimum Premium'),
    tax_status_uuid: i18n.t('Tax Status'),
    tax_rate: i18n.t('Tax Rate'),
    payment_terms: i18n.t('Payment Terms'),
    settlement_days: i18n.t('Settlement Days'),
    unsold_fee: i18n.t('Unsold Fee'),
    withdrawal_fee: i18n.t('Withdrawal Fee'),
    insurance_rate: i18n.t('L/D Warranty'),
    lot_handling_charge: i18n.t('Entry Fee'),
    invoice_prefix: i18n.t('Invoice Prefix'),
    receipt_prefix: i18n.t('Receipt Prefix'),
    settlement_prefix: i18n.t('Settlement Prefix'),
    batch_payment_prefix: i18n.t('Batch Payment Prefix'),
    vendor_settlement_footnote: i18n.t('Vendor Settlement Footnote'),
    buyer_invoice_footnote: i18n.t('Buyer Invoice Footnote'),
    enable_estimate_request: i18n.t('Enable Estimate Request'),
    automatic_deposit_allocation: i18n.t('Automatic Deposit Allocation'),
    increment_calculation_source: i18n.t('Increment Calculation Source'),
    auto_approve_invoices: i18n.t('Auto Approve Invoices'),
    auto_approve_settlements: i18n.t('Auto Approve Settlement'),
    enable_trust_levels: i18n.t('Enable Trust Levels'),
    credit_limit: i18n.t('Bidding Limit'),
    credit_limit_type: i18n.t('Bidding Limit Type'),
    important_information: i18n.t('Important Information'),
    payment_information: i18n.t('Payment Information'),
    viewing_information: i18n.t('Viewing Information'),
    collection_information: i18n.t('Collection Information'),
    terms_and_conditions: i18n.t('Terms and Conditions'),
    auction_location: i18n.t('Auction Location'),
    auction_contact_information: i18n.t('Contact Information'),
    auctioneers_sheet_prefix: i18n.t("Auctioneer's Sheet Prefix"),
    transfer_note_prefix: i18n.t('Transfer Note Prefix'),
    email: i18n.t('Email'),
    telephone_bid_request_cut_off: i18n.t('Telephone Bid Request Cut off (Minutes before Start Time)'),
    commission_bid_request_cut_off: i18n.t('Commission Bid Request Cut off (Minutes before Start Time)'),
    bidder_registration_cut_off: i18n.t('Bidder Registration Cut off (Minutes before Start Time)'),
    allow_webapp_condition_report_request: i18n.t('Allow Condition Report Requests Through Webapp'),
  };

  toJSON() {
    let data = cloneDeep(super.toJSON());
    data = helperService.calculateTiers(data, data.tax_rate)

    if (data.contract_default_recipients && data.contract_default_recipients.length > 0) {
      data.contract_default_recipients = data.contract_default_recipients.map(i => {
        let tmpData = cloneDeep(i)
        delete tmpData.uuid
        return tmpData
      })
    }

    return data
  }
}

defineTranslatableProperties(BranchModel);
